/**
 * Report Template Selector - Unified Component
 * Grid view of available report templates
 */

import React from 'react';
import { Card, Button } from '../UI/Card';
import { Button as ButtonComponent } from '../UI/Button';
import { ReportTemplate } from '../../types/reports';
import { FileText } from 'lucide-react';

interface ReportTemplateSelectorProps {
  templates: ReportTemplate[];
  onSelectTemplate: (template: ReportTemplate) => void;
  onCustomReport: () => void;
  title?: string;
  description?: string;
}

export function ReportTemplateSelector({ 
  templates, 
  onSelectTemplate, 
  onCustomReport,
  title = 'Choose a Report Template',
  description = 'Select a pre-configured template or build a custom report from scratch',
}: ReportTemplateSelectorProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {title}
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          {description}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <Card 
            key={template.id}
            className="hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => onSelectTemplate(template)}
          >
            <div className="p-6 space-y-4">
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${template.color} flex items-center justify-center`}>
                <template.icon className="w-6 h-6 text-white" />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {template.name}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {template.description}
                </p>
              </div>

              <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Frequency:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {template.regulatoryContext.frequency}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Deadline:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {template.regulatoryContext.deadline}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Questions:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {template.questions.length}
                  </span>
                </div>
              </div>

              <ButtonComponent 
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectTemplate(template);
                }}
              >
                Use Template
              </ButtonComponent>
            </div>
          </Card>
        ))}

        {/* Custom Report Option */}
        <Card 
          className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-dashed border-gray-300 dark:border-gray-600"
          onClick={onCustomReport}
        >
          <div className="p-6 space-y-4 flex flex-col items-center justify-center h-full">
            <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <FileText className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            </div>

            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Custom Report
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Build a custom report with your own questions and structure
              </p>
            </div>

            <ButtonComponent 
              variant="outline"
              className="w-full"
              onClick={(e) => {
                e.stopPropagation();
                onCustomReport();
              }}
            >
              Build Custom Report
            </ButtonComponent>
          </div>
        </Card>
      </div>
    </div>
  );
}











